<template>
	<div class="layout-preview" :class="{ '-open': isOpen }">
		<button
			type="button"
			class="layout-preview__toggle"
			data-test-id="preview-toggle"
			@click="isOpen = !isOpen"
		>
			<span class="layout-preview__head">
				<span class="layout-preview__title">Preview layout</span>
				<span class="layout-preview__hint">ตัวอย่างการวางเลย์เอาต์ตามค่า Header / Sidebar / Container ที่เลือก</span>
			</span>
			<CIcon
				name="cil-chevron-bottom"
				class="layout-preview__chevron"
				:class="{ '-open': isOpen }"
			/>
		</button>

		<CCollapse :show="isOpen">
			<div class="layout-preview__body">
				<div class="layout-preview__frames">
					<!-- Desktop -->
					<figure class="device device--desktop">
						<div class="device__screen" data-test-id="desktop-screen">
							<div class="device__topbar">
								<span class="dot"></span>
								<span class="dot"></span>
								<span class="dot"></span>
							</div>
							<div class="device__viewport">
								<div
									class="page"
									:class="{ '-constrained': isConstrained }"
									:style="pagePaddingStyle"
									data-test-id="desktop-page"
								>
									<aside
										v-if="sidebarVisibility"
										class="page__sidebar"
										data-test-id="desktop-sidebar"
									>
										<span class="bar -xs"></span>
										<span class="bar -xs"></span>
										<span class="bar -xs"></span>
										<span class="bar -xs"></span>
									</aside>
									<section class="page__content">
										<span
											v-if="headerVisibility"
											class="bar -title"
											data-test-id="desktop-title"
										></span>
										<span class="bar"></span>
										<span class="bar"></span>
										<span class="bar -w70"></span>
									</section>
								</div>
							</div>
						</div>
						<figcaption class="device__caption">
							<span class="device__name">Desktop</span>
							<span class="device__state">{{ desktopStateText }}</span>
						</figcaption>
					</figure>

					<!-- Mobile -->
					<figure class="device device--mobile">
						<div class="device__screen" data-test-id="mobile-screen">
							<div class="device__notch"></div>
							<div class="device__viewport">
								<div
									class="page"
									:class="{ '-constrained': isConstrained }"
									:style="pagePaddingStyle"
									data-test-id="mobile-page"
								>
									<div
										v-if="sidebarVisibility"
										class="page__menu-btn"
										data-test-id="mobile-menu-button"
									>
										<span class="burger"></span>
										<span class="bar -menu"></span>
									</div>
									<section class="page__content">
										<!-- page title ถูกซ่อนบนมือถือตามดีไซน์ปัจจุบัน -->
										<span class="bar"></span>
										<span class="bar"></span>
										<span class="bar"></span>
										<span class="bar -w70"></span>
									</section>
								</div>
							</div>
						</div>
						<figcaption class="device__caption">
							<span class="device__name">Mobile</span>
							<span class="device__state">{{ mobileStateText }}</span>
						</figcaption>
					</figure>
				</div>

				<p class="layout-preview__note">
					หมายเหตุ: บนมือถือ ชื่อหน้า (page title) จะไม่แสดงตามดีไซน์ปัจจุบัน และ Sidebar จะกลายเป็นปุ่มเมนู
				</p>
			</div>
		</CCollapse>
	</div>
</template>

<script>
export default {
	name: 'StaticPageLayoutPreview',

	props: {
		headerVisibility: {
			type: Boolean,
			default: false,
		},
		sidebarVisibility: {
			type: Boolean,
			default: false,
		},
		container: {
			type: Boolean,
			default: false,
		},
		containerPadding: {
			type: Object,
			default: () => ({ top: true, right: true, bottom: true, left: true }),
		},
	},

	data() {
		return {
			isOpen: false,
		};
	},

	computed: {
		// Desktop content is constrained (has side gutters) when the sidebar is shown
		// (parent wraps it in .container) OR the admin turns Container on.
		isConstrained() {
			return this.sidebarVisibility || this.container;
		},
		desktopStateText() {
			return [
				this.headerVisibility ? 'มีหัวข้อหน้า' : 'ไม่มีหัวข้อหน้า',
				this.sidebarVisibility ? 'มี Sidebar' : 'ไม่มี Sidebar',
				this.isConstrained ? 'จำกัดความกว้าง' : 'เต็มความกว้าง',
			].join(' · ');
		},
		mobileStateText() {
			return [
				'ไม่แสดงหัวข้อหน้า',
				this.sidebarVisibility ? 'มีปุ่มเมนู' : 'ไม่มีเมนู',
				'เต็มความกว้าง',
			].join(' · ');
		},
		pagePaddingStyle() {
			const cp = this.containerPadding || {};
			return {
				paddingTop: cp.top === false ? '0' : null,
				paddingRight: cp.right === false ? '0' : null,
				paddingBottom: cp.bottom === false ? '0' : null,
				paddingLeft: cp.left === false ? '0' : null,
			};
		},
	},
};
</script>

<style lang="scss" scoped>
	.layout-preview {
		margin-top: rem(4);
		border: rem(1) solid $color-gray-300;
		border-radius: rem(8);
		background-color: $color-gray-100;

		&__toggle {
			display: flex;
			align-items: center;
			justify-content: space-between;
			width: 100%;
			padding: rem(14) rem(16);
			border: 0;
			background-color: transparent;
			text-align: left;
			cursor: pointer;

			&:focus,
			&:focus-visible {
				outline: none;
				box-shadow: none;
			}
		}

		&__head {
			display: flex;
			flex-direction: column;
		}

		&__title {
			font-size: rem(14);
			font-weight: $font-weight-bold;
			color: $color-black-90;
		}

		&__hint {
			margin-top: rem(2);
			font-size: rem(12);
			color: $color-black-45;
		}

		&__chevron {
			flex-shrink: 0;
			margin-left: rem(12);
			color: $color-gray-600;
			transition: transform 0.2s ease;

			&.-open {
				transform: rotate(180deg);
			}
		}

		&__body {
			padding: 0 rem(16) rem(16);
		}

		&__frames {
			display: flex;
			flex-wrap: nowrap;
			align-items: flex-start;
			gap: rem(24);
			overflow-x: auto;
		}

		&__note {
			margin: rem(16) 0 0;
			font-size: rem(12);
			color: $color-black-45;
		}
	}

	.device {
		margin: 0;
		flex-shrink: 0;

		&__screen {
			overflow: hidden;
			border: rem(1) solid $color-gray-400;
			border-radius: rem(8);
			background-color: $color-white;
		}

		&--desktop &__screen {
			width: rem(360);
		}

		&--mobile &__screen {
			width: rem(150);
			border-radius: rem(16);
		}

		&__topbar {
			display: flex;
			align-items: center;
			gap: rem(4);
			padding: rem(6) rem(8);
			background-color: $color-gray-200;
		}

		&__notch {
			height: rem(14);
			background-color: $color-gray-200;

			&::after {
				content: "";
				display: block;
				width: rem(40);
				height: rem(4);
				margin: rem(5) auto 0;
				border-radius: rem(4);
				background-color: $color-gray-400;
			}
		}

		&__viewport {
			min-height: rem(160);
			background-color: $color-gray-100;
		}

		&--mobile &__viewport {
			min-height: rem(220);
		}

		&__caption {
			margin-top: rem(8);
			text-align: center;
		}

		&__name {
			display: block;
			font-size: rem(12);
			font-weight: $font-weight-bold;
			color: $color-black-70;
		}

		&__state {
			display: block;
			margin-top: rem(2);
			font-size: rem(11);
			color: $color-black-45;
		}
	}

	.dot {
		width: rem(8);
		height: rem(8);
		border-radius: 50%;
		background-color: $color-gray-400;
	}

	.page {
		display: flex;
		gap: rem(8);
		min-height: rem(160);
		background-color: $color-white;

		&.-constrained {
			max-width: rem(280);
			padding: rem(12);
			margin: 0 auto;
		}

		&__sidebar {
			display: flex;
			flex: 0 0 rem(64);
			flex-direction: column;
			gap: rem(6);
			padding: rem(8);
			border-radius: rem(4);
			background-color: $color-gray-200;
		}

		&__content {
			display: flex;
			flex: 1;
			flex-direction: column;
			gap: rem(8);
			min-width: 0;
		}

		&__menu-btn {
			display: flex;
			align-items: center;
			gap: rem(6);
			padding: rem(6);
			margin-bottom: rem(4);
			border-radius: rem(4);
			background-color: $color-gray-200;
		}
	}

	.device--mobile .page {
		flex-direction: column;
		gap: rem(8);
		min-height: rem(220);
	}

	.burger {
		position: relative;
		width: rem(12);
		height: rem(2);
		background-color: $color-gray-600;

		&::before,
		&::after {
			content: "";
			position: absolute;
			left: 0;
			width: rem(12);
			height: rem(2);
			background-color: $color-gray-600;
		}

		&::before {
			top: rem(-4);
		}

		&::after {
			top: rem(4);
		}
	}

	.bar {
		display: block;
		height: rem(8);
		border-radius: rem(2);
		background-color: $color-gray-300;

		&.-w70 {
			width: 70%;
		}

		&.-xs {
			height: rem(6);
		}

		&.-title {
			height: rem(12);
			width: 55%;
			background-color: $color-gray-500;
		}

		&.-menu {
			height: rem(6);
			width: rem(48);
		}
	}
</style>
